import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { queryClient } from '@/lib/queryClient';

// ============================================================================
// TYPES (State Machine Architecture)
// ============================================================================

export type PlanType = 'starter' | 'pro' | 'business' | null;
export type SubscriptionStatusType = 'pending' | 'trial' | 'active' | 'cancelled' | 'expired';

export interface ProfileData {
  subscriptionStatus: SubscriptionStatusType;
  subscriptionPlan: PlanType;
  emailVerified: boolean;
  firstName: string | null;
  lastName: string | null;
  businessName: string | null;
}

export interface EstablishmentData {
  id: string;
  name: string;
  googlePlaceId: string | null;
  googleReviewsLink: string | null;
  logoUrl: string | null;
  city: string | null;
}

// ============================================================================
// CONTEXT TYPE (State Machine)
// ============================================================================

export interface AppStateContextType {
  // Auth (Core)
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isGoogleUser: boolean;
  
  // ⭐ STATE MACHINE - 4 Key States
  isEmailVerified: boolean;        // email_confirmed_at exists OR provider is 'google'
  hasActiveSubscription: boolean;  // subscription_status is 'active' or 'trial'
  isConfigured: boolean;           // has at least 1 establishment
  isOnboardingComplete: boolean;   // onboarding_step is 'completed'
  onboardingStep: string | null;   // current onboarding step
  
  // Profile Data
  subscriptionStatus: SubscriptionStatusType | null;
  subscriptionPlan: PlanType;
  firstName: string | null;
  lastName: string | null;
  businessName: string | null;
  
  // Establishments
  establishments: EstablishmentData[];
  currentEstablishment: EstablishmentData | null;
  
  // Loading States
  isInitializing: boolean;
  isLoadingProfile: boolean;
  isLoadingEstablishments: boolean;
  
  // Error States
  profileError: string | null;
  establishmentError: string | null;
  
  // Actions
  signOut: () => Promise<void>;
  refreshState: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshEstablishments: () => Promise<void>;
  setCurrentEstablishment: (id: string) => void;
  clearErrors: () => void;
  forceCleanSession: () => Promise<void>;
}

// ============================================================================
// CONTEXT
// ============================================================================

const AppStateContext = createContext<AppStateContextType | undefined>(undefined);

// ============================================================================
// HELPER: Clean localStorage & Session Storage
// ============================================================================

const cleanAllStorage = () => {
  console.log('[AppState] 🧹 Cleaning all storage...');
  
  // Clear localStorage
  localStorage.clear();
  
  // Clear sessionStorage
  sessionStorage.clear();
  
  // Clear Supabase specific keys
  const keysToRemove = Object.keys(localStorage).filter(key => 
    key.startsWith('sb-') || 
    key.includes('supabase') ||
    key.includes('auth')
  );
  keysToRemove.forEach(key => localStorage.removeItem(key));
};

// ============================================================================
// PROVIDER (State Machine Architecture)
// ============================================================================

export const AppStateProvider = ({ children }: { children: ReactNode }) => {
  // Auth state
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  
  // Profile state
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatusType | null>(null);
  const [subscriptionPlan, setSubscriptionPlan] = useState<PlanType>(null);
  const [profileEmailVerified, setProfileEmailVerified] = useState(false);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [onboardingStep, setOnboardingStep] = useState<string | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileNotFound, setProfileNotFound] = useState(false);
  
  // Establishments state
  const [establishments, setEstablishments] = useState<EstablishmentData[]>([]);
  const [currentEstablishment, setCurrentEstablishmentState] = useState<EstablishmentData | null>(null);
  const [isLoadingEstablishments, setIsLoadingEstablishments] = useState(false);
  const [establishmentError, setEstablishmentError] = useState<string | null>(null);
  
  // Prevent duplicate initialization
  const initRef = useRef(false);
  const fetchingRef = useRef(false);
  const orphanCheckRef = useRef(false);
  // Refs to access current values inside auth callback without re-subscribing
  const userRef = useRef<User | null>(null);
  const subscriptionStatusRef = useRef<SubscriptionStatusType | null>(null);

  // Keep refs in sync with state (for use inside auth callback without re-subscribing)
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { subscriptionStatusRef.current = subscriptionStatus; }, [subscriptionStatus]);

  // ============================================================================
  // DERIVED STATES (State Machine)
  // ============================================================================
  
  const isAuthenticated = !!user;
  const isGoogleUser = user?.app_metadata?.provider === 'google';
  
  // ⭐ STATE 1: Email Verified
  // Google users are auto-verified, email users need email_confirmed_at
  const isEmailVerified = isGoogleUser || !!user?.email_confirmed_at || profileEmailVerified;
  
  // ⭐ STATE 2: Has Active Subscription
  const hasActiveSubscription = subscriptionStatus === 'active' || subscriptionStatus === 'trial';
  
  // ⭐ STATE 3: Is Configured (has establishments)
  const isConfigured = establishments.length > 0;
  
  // ⭐ STATE 4: Onboarding Complete
  const isOnboardingComplete = onboardingStep === 'completed';

  // ============================================================================
  // FORCE CLEAN SESSION (Public Action)
  // ============================================================================
  
  const forceCleanSession = useCallback(async () => {
    console.log('[AppState] 🔥 Force cleaning session...');
    
    try {
      await supabase.auth.signOut({ scope: 'global' });
    } catch (err) {
      console.error('[AppState] ❌ SignOut error:', err);
    }
    
    // Clean all storage
    cleanAllStorage();
    
    // Reset all state
    setUser(null);
    setSession(null);
    setSubscriptionStatus(null);
    setSubscriptionPlan(null);
    setProfileEmailVerified(false);
    setFirstName(null);
    setLastName(null);
    setBusinessName(null);
    setEstablishments([]);
    setCurrentEstablishmentState(null);
    setProfileError(null);
    setEstablishmentError(null);
    setProfileNotFound(false);
  }, []);

  // ============================================================================
  // FETCH PROFILE (with orphan detection)
  // ============================================================================
  
  const fetchProfile = useCallback(async (userId: string): Promise<boolean> => {
    if (fetchingRef.current) return true;
    fetchingRef.current = true;
    
    console.log('[AppState] 📋 Fetching profile for:', userId);
    setIsLoadingProfile(true);
    setProfileError(null);
    setProfileNotFound(false);
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          subscription_status,
          subscription_plan,
          email_verified,
          first_name,
          last_name,
          business_name,
          onboarding_step
        `)
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('[AppState] ❌ Profile fetch error:', error);
        setProfileError('Impossible de charger le profil');
        fetchingRef.current = false;
        return false;
      }

      if (data) {
        console.log('[AppState] ✅ Profile loaded:', {
          status: data.subscription_status,
          plan: data.subscription_plan,
          emailVerified: data.email_verified,
        });
        
        setSubscriptionStatus((data.subscription_status as SubscriptionStatusType) || 'pending');
        setSubscriptionPlan((data.subscription_plan as PlanType) || null);
        setProfileEmailVerified(data.email_verified === true);
        setFirstName(data.first_name);
        setLastName(data.last_name);
        setBusinessName(data.business_name);
        setOnboardingStep(data.onboarding_step || 'pending');
        setProfileNotFound(false);
        fetchingRef.current = false;
        return true;
      } else {
        // No profile yet (common before payment / first login). Create a minimal one.
        console.warn('[AppState] ⚠️ Profile missing, attempting auto-create:', userId);

        try {
          const { data: authData, error: authErr } = await supabase.auth.getUser();
          if (authErr) throw authErr;

          const authUser = authData.user;
          const meta = (authUser?.user_metadata ?? {}) as Record<string, any>;

          const isGoogleProvider =
            authUser?.app_metadata?.provider === 'google' ||
            (Array.isArray(authUser?.app_metadata?.providers) &&
              authUser?.app_metadata?.providers.includes('google'));

          const payload = {
            user_id: userId,
            business_name: (meta.business_name as string) || 'À configurer',
            first_name: (meta.first_name as string) ?? null,
            last_name: (meta.last_name as string) ?? null,
            job_title: (meta.job_title as string) ?? null,
            language: (meta.language as string) ?? null,
            notification_email: authUser?.email ?? null,
            subscription_status: 'pending',
            subscription_plan: null,
            // IMPORTANT: auto-verify only for Google OAuth users
            email_verified: isGoogleProvider,
          };

          const { error: createErr } = await supabase
            .from('profiles')
            .upsert(payload, { onConflict: 'user_id' });

          if (createErr) throw createErr;

          console.log('[AppState] ✅ Minimal profile created');

          // Hydrate local state immediately (no need to re-fetch)
          setSubscriptionStatus('pending');
          setSubscriptionPlan(null);
          setProfileEmailVerified(payload.email_verified === true);
          setFirstName(payload.first_name);
          setLastName(payload.last_name);
          setBusinessName(payload.business_name);
          setProfileNotFound(false);
          fetchingRef.current = false;
          return true;
        } catch (createProfileErr) {
          console.error('[AppState] ❌ Failed to auto-create profile:', createProfileErr);
          setProfileNotFound(true);
          setProfileError('Impossible de créer le profil');
          fetchingRef.current = false;
          return false;
        }
      }
    } catch (err) {
      console.error('[AppState] ❌ Profile fetch exception:', err);
      setProfileError('Erreur de connexion au serveur');
      fetchingRef.current = false;
      return false;
    } finally {
      setIsLoadingProfile(false);
    }
  }, []);

  // ============================================================================
  // ORPHAN SESSION AUTO-CLEAN
  // ============================================================================
  
  useEffect(() => {
    // Only trigger when we have a user, profile loading is done, and profile was not found
    if (!user || isLoadingProfile || !profileNotFound || orphanCheckRef.current) {
      return;
    }

    // Do NOT auto-signout while the user is in the payment/onboarding tunnel.
    // In this phase a profile may legitimately not exist yet.
    const path = window.location.pathname;
    const isInPaymentFlow =
      path.startsWith('/choose-plan') ||
      path.startsWith('/success') ||
      path.startsWith('/verify-email');

    if (isInPaymentFlow) {
      console.warn('[AppState] ⚠️ Profile missing during payment flow - skipping auto-clean');
      return;
    }
    
    orphanCheckRef.current = true;
    
    console.log('[AppState] 🚨 Auto-cleaning orphan session...');
    toast.error('Session invalide', {
      description: 'Votre session a expiré. Veuillez vous reconnecter.',
    });
    
    // Force clean and redirect
    forceCleanSession().then(() => {
      // Navigation will be handled by RouteGuard
      window.location.href = '/login';
    });
  }, [user, isLoadingProfile, profileNotFound, forceCleanSession]);

  // ============================================================================
  // FETCH ESTABLISHMENTS (with error handling)
  // ============================================================================
  
  const fetchEstablishments = useCallback(async (userId: string) => {
    console.log('[AppState] 🏢 Checking establishment count for:', userId);
    setIsLoadingEstablishments(true);
    setEstablishmentError(null);
    
    try {
      // 🔧 OPTIMIZATION: Only fetch count for isConfigured check
      // Full establishment data is handled by useEstablishments hook with React Query caching
      const { count, error } = await supabase
        .from('establishments')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (error) {
        console.error('[AppState] ❌ Establishments count error:', error);
        setEstablishmentError('Impossible de charger les établissements');
        return;
      }

      const establishmentCount = count || 0;
      console.log('[AppState] ✅ Establishment count:', establishmentCount);
      
      // Create minimal placeholder array just for isConfigured calculation
      // Real data comes from useEstablishments hook
      if (establishmentCount > 0) {
        setEstablishments(Array(establishmentCount).fill({ id: 'placeholder' }) as any);
      } else {
        setEstablishments([]);
      }
      setCurrentEstablishmentState(null); // Let useEstablishments handle selection
      
    } catch (err) {
      console.error('[AppState] ❌ Establishments count exception:', err);
      setEstablishmentError('Erreur de connexion');
    } finally {
      setIsLoadingEstablishments(false);
    }
  }, []);

  // ============================================================================
  // ACTIONS
  // ============================================================================
  
  const signOut = useCallback(async () => {
    console.log('[AppState] 🚪 Signing out with full cleanup...');
    
    try {
      await supabase.auth.signOut({ scope: 'global' });
    } catch (err) {
      console.error('[AppState] ❌ SignOut error:', err);
    }
    
    // Clean all storage
    cleanAllStorage();
    
    // Reset all state
    setUser(null);
    setSession(null);
    setSubscriptionStatus(null);
    setSubscriptionPlan(null);
    setProfileEmailVerified(false);
    setFirstName(null);
    setLastName(null);
    setBusinessName(null);
    setEstablishments([]);
    setCurrentEstablishmentState(null);
    setProfileError(null);
    setEstablishmentError(null);
    setProfileNotFound(false);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  }, [user, fetchProfile]);

  const refreshEstablishments = useCallback(async () => {
    if (user) {
      await fetchEstablishments(user.id);
    }
  }, [user, fetchEstablishments]);

  const refreshState = useCallback(async () => {
    console.log('[AppState] 🔄 Refreshing full state...');
    if (user) {
      await Promise.all([
        fetchProfile(user.id),
        fetchEstablishments(user.id),
      ]);
    }
  }, [user, fetchProfile, fetchEstablishments]);

  const setCurrentEstablishment = useCallback((id: string) => {
    const found = establishments.find(e => e.id === id);
    if (found) {
      setCurrentEstablishmentState(found);
    }
  }, [establishments]);

  const clearErrors = useCallback(() => {
    setProfileError(null);
    setEstablishmentError(null);
  }, []);

  // ============================================================================
  // INITIALIZATION (State Machine Entry Point)
  // ============================================================================
  
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    
    console.log('[AppState] 🚀 Initializing State Machine...');
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log('[AppState] 🔔 Auth event:', event);
        
        // 🔧 CRITICAL: Avoid marking initialization as done on INITIAL_SESSION.
        if (event === 'INITIAL_SESSION') {
          return;
        }

        // 🔧 FIX: For TOKEN_REFRESHED, do NOTHING — no setSession, no setUser.
        // Supabase triggers this on every visibilitychange (hidden→visible).
        // Any setState here re-renders the entire context tree, which causes
        // a visible loader flash. The session object in memory is already updated
        // by the Supabase client internally; consumers that need the fresh token
        // can call supabase.auth.getSession() on demand.
        if (event === 'TOKEN_REFRESHED') {
          console.log('[AppState] 🔇 TOKEN_REFRESHED ignored (no re-render)');
          return;
        }

        // 🔧 FIX: Supabase can re-emit SIGNED_IN on visibility change (tab focus)
        // even for the same user. If the user is already loaded and it's the same
        // user ID, skip ALL state updates to prevent re-renders and loading flashes.
        const incomingUserId = currentSession?.user?.id;
        const currentUserId = userRef.current?.id;

        if (event === 'SIGNED_IN' && incomingUserId && incomingUserId === currentUserId && subscriptionStatusRef.current !== null) {
          console.log('[AppState] 🔇 SIGNED_IN ignored (same user already loaded)');
          return;
        }
        
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        // Reset orphan check flag on new auth events
        orphanCheckRef.current = false;
        
        // Only fetch data on SIGNED_IN for a NEW user (not a re-emit on focus)
        if (event === 'SIGNED_IN' && currentSession?.user) {
          console.log('[AppState] ✅ User signed in, fetching data...');
          // Mark browser session as active
          sessionStorage.setItem('ot_browser_session', '1');
          // Use setTimeout to avoid blocking the auth callback
          setTimeout(() => {
            fetchProfile(currentSession.user.id);
            fetchEstablishments(currentSession.user.id);
          }, 0);
        }
        
        // Clear state AND React Query cache when user signs out
        if (event === 'SIGNED_OUT') {
          console.log('[AppState] 🚪 User signed out, clearing state and query cache...');
          
          // 🔥 Clear React Query cache to prevent ghost session data
          queryClient.clear();
          
          setSubscriptionStatus(null);
          setSubscriptionPlan(null);
          setEstablishments([]);
          setCurrentEstablishmentState(null);
          setProfileNotFound(false);
        }
        
        setIsInitializing(false);
      }
    );

    // Check current session
    supabase.auth.getSession().then(async ({ data: { session: currentSession } }) => {
      console.log('[AppState] 📋 Initial session check:', currentSession ? 'Found' : 'None');
      
      // 🔒 FORCE RE-LOGIN: If the browser was closed and reopened,
      // sessionStorage will be empty → sign the user out so they must log in again.
      // sessionStorage is automatically cleared when the browser/tab is fully closed.
      if (currentSession?.user && !sessionStorage.getItem('ot_browser_session')) {
        console.log('[AppState] 🔒 Browser session expired (browser was closed). Forcing re-login...');
        await supabase.auth.signOut();
        cleanAllStorage();
        setSession(null);
        setUser(null);
        setIsInitializing(false);
        return;
      }
      
      // Mark this browser session as active
      if (currentSession?.user) {
        sessionStorage.setItem('ot_browser_session', '1');
      }
      
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      
      if (currentSession?.user) {
        fetchProfile(currentSession.user.id);
        fetchEstablishments(currentSession.user.id);
      }
      
      setIsInitializing(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile, fetchEstablishments]);

  // ============================================================================
  // DEBUG LOGGING
  // ============================================================================
  
  useEffect(() => {
    if (!isInitializing) {
      console.log('[AppState] 🎯 State Machine Status:', {
        isAuthenticated,
        isEmailVerified,
        hasActiveSubscription,
        isConfigured,
        subscriptionStatus,
        establishmentCount: establishments.length,
        profileNotFound,
      });
    }
  }, [isAuthenticated, isEmailVerified, hasActiveSubscription, isConfigured, subscriptionStatus, establishments.length, isInitializing, profileNotFound]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  
  const value: AppStateContextType = {
    // Auth
    user,
    session,
    isAuthenticated,
    isGoogleUser,
    
    // State Machine States
    isEmailVerified,
    hasActiveSubscription,
    isConfigured,
    isOnboardingComplete,
    onboardingStep,
    
    // Profile
    subscriptionStatus,
    subscriptionPlan,
    firstName,
    lastName,
    businessName,
    
    // Establishments
    establishments,
    currentEstablishment,
    
    // Loading
    isInitializing,
    isLoadingProfile,
    isLoadingEstablishments,
    
    // Errors
    profileError,
    establishmentError,
    
    // Actions
    signOut,
    refreshState,
    refreshProfile,
    refreshEstablishments,
    setCurrentEstablishment,
    clearErrors,
    forceCleanSession,
  };

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

export const useAppState = () => {
  const context = useContext(AppStateContext);
  if (context === undefined) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
};
